/**
 * Futures (COIN-M) REST API
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type { KeepaliveUserDataStreamResponse, StartUserDataStreamResponse } from '../types';

/**
 * UserDataStreamsApi - axios parameter creator
 */
const UserDataStreamsApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Close out a user data stream.
         *
         * Weight(IP): 1
         *
         * Security Type: USER_STREAM
         *
         * @summary Close User Data Stream (USER_STREAM)
         *
         * @throws {RequiredError}
         */
        closeUserDataStream: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/listenKey',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Keepalive a user data stream to prevent a time out. User data streams
         * will close after 60 minutes.
         *
         * Weight(IP): 1
         *
         * Security Type: USER_STREAM
         *
         * @summary Keepalive User Data Stream (USER_STREAM)
         *
         * @throws {RequiredError}
         */
        keepaliveUserDataStream: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/listenKey',
                method: 'PUT',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Start a new user data stream. The stream will close after 60 minutes
         * unless a keepalive is sent. If the account has an active `listenKey`,
         * that `listenKey` will be returned and its validity will be extended for
         * 60 minutes.
         *
         * Weight(IP): 1
         *
         * Security Type: USER_STREAM
         *
         * @summary Start User Data Stream (USER_STREAM)
         *
         * @throws {RequiredError}
         */
        startUserDataStream: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/listenKey',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * UserDataStreamsApi - interface
 * @interface UserDataStreamsApi
 */
export interface UserDataStreamsApiInterface {
    /**
     * Close out a user data stream.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Close User Data Stream (USER_STREAM)
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApiInterface
     */
    closeUserDataStream(): Promise<RestApiResponse<void>>;
    /**
     * Keepalive a user data stream to prevent a time out. User data streams
     * will close after 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Keepalive User Data Stream (USER_STREAM)
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApiInterface
     */
    keepaliveUserDataStream(): Promise<RestApiResponse<KeepaliveUserDataStreamResponse>>;
    /**
     * Start a new user data stream. The stream will close after 60 minutes
     * unless a keepalive is sent. If the account has an active `listenKey`,
     * that `listenKey` will be returned and its validity will be extended for
     * 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Start User Data Stream (USER_STREAM)
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApiInterface
     */
    startUserDataStream(): Promise<RestApiResponse<StartUserDataStreamResponse>>;
}

/**
 * UserDataStreamsApi - object-oriented interface
 * @class UserDataStreamsApi
 */
export class UserDataStreamsApi implements UserDataStreamsApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = UserDataStreamsApiAxiosParamCreator(configuration);
    }

    /**
     * Close out a user data stream.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Close User Data Stream (USER_STREAM)
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#close-user-data-stream Binance API Documentation}
     */
    public async closeUserDataStream(): Promise<RestApiResponse<void>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.closeUserDataStream();
        return sendRequest<void>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Keepalive a user data stream to prevent a time out. User data streams
     * will close after 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Keepalive User Data Stream (USER_STREAM)
     * @returns {Promise<RestApiResponse<KeepaliveUserDataStreamResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#keepalive-user-data-stream Binance API Documentation}
     */
    public async keepaliveUserDataStream(): Promise<
        RestApiResponse<KeepaliveUserDataStreamResponse>
        > {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.keepaliveUserDataStream();
        return sendRequest<KeepaliveUserDataStreamResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Start a new user data stream. The stream will close after 60 minutes
     * unless a keepalive is sent. If the account has an active `listenKey`,
     * that `listenKey` will be returned and its validity will be extended for
     * 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Start User Data Stream (USER_STREAM)
     * @returns {Promise<RestApiResponse<StartUserDataStreamResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof UserDataStreamsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#start-user-data-stream Binance API Documentation}
     */
    public async startUserDataStream(): Promise<RestApiResponse<StartUserDataStreamResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.startUserDataStream();
        return sendRequest<StartUserDataStreamResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }
}
